import { communityApiEndpoints, communityApiPost } from '@/lib/communityApi'
import type { CommunityVoteDirection } from '@/lib/communityPostData'

export const DEFAULT_COMMUNITY_COMMENTS_PAGE_LIMIT = 20
export const COMMUNITY_COMMENT_MAX_LENGTH = 2000

export interface CommunityComment {
  readonly id: string
  readonly postId: string
  readonly parentCommentId: string | null
  readonly rootCommentId: string | null
  readonly depth: number
  readonly userId: string
  readonly authorDisplayName?: string | null
  readonly body: string
  readonly upvoteCount: number
  readonly downvoteCount: number
  readonly score: number
  readonly replyCount: number
  readonly createdAt: Date
}

interface CommunityCommentApiPayload {
  readonly id: string
  readonly postId: string
  readonly parentCommentId?: string | null
  readonly rootCommentId?: string | null
  readonly depth: number
  readonly userId: string
  readonly authorDisplayName?: string | null
  readonly body: string
  readonly upvoteCount: number
  readonly downvoteCount: number
  readonly score: number
  readonly replyCount: number
  readonly createdAt: string
}

function parseCommunityComment(payload: CommunityCommentApiPayload): CommunityComment {
  return {
    id: payload.id,
    postId: payload.postId,
    parentCommentId: payload.parentCommentId ?? null,
    rootCommentId: payload.rootCommentId ?? null,
    depth: payload.depth,
    userId: payload.userId,
    authorDisplayName: payload.authorDisplayName ?? null,
    body: payload.body,
    upvoteCount: payload.upvoteCount,
    downvoteCount: payload.downvoteCount,
    score: payload.score,
    replyCount: payload.replyCount,
    createdAt: new Date(payload.createdAt),
  }
}

interface CommunityCommentsPageResponse {
  readonly comments: CommunityCommentApiPayload[]
  readonly nextCursor: string | null
}

export async function fetchCommunityCommentsPage(params: {
  readonly postId: string
  readonly parentCommentId?: string | null
  readonly pageLimit?: number
}): Promise<{ comments: CommunityComment[]; nextCursor: string | null }> {
  const response = await communityApiPost<CommunityCommentsPageResponse>(
    communityApiEndpoints.commentsQuery,
    {
      postId: params.postId,
      parentCommentId: params.parentCommentId ?? null,
      limit: params.pageLimit ?? DEFAULT_COMMUNITY_COMMENTS_PAGE_LIMIT,
    },
  )

  return {
    comments: response.comments.map(parseCommunityComment),
    nextCursor: response.nextCursor,
  }
}

export async function createCommunityComment(payload: {
  readonly postId: string
  readonly parentCommentId?: string | null
  readonly userId: string
  readonly authorDisplayName?: string | null
  readonly body: string
}): Promise<CommunityComment> {
  const response = await communityApiPost<{ comment: CommunityCommentApiPayload | null; commentId: string }>(
    communityApiEndpoints.comments,
    {
      postId: payload.postId,
      parentCommentId: payload.parentCommentId ?? null,
      authorDisplayName: payload.authorDisplayName ?? null,
      body: payload.body,
    },
    true,
  )

  if (response.comment) {
    return parseCommunityComment(response.comment)
  }

  return {
    id: response.commentId,
    postId: payload.postId,
    parentCommentId: payload.parentCommentId ?? null,
    rootCommentId: payload.parentCommentId ?? null,
    depth: payload.parentCommentId ? 1 : 0,
    userId: payload.userId,
    authorDisplayName: payload.authorDisplayName ?? null,
    body: payload.body.trim(),
    upvoteCount: 0,
    downvoteCount: 0,
    score: 0,
    replyCount: 0,
    createdAt: new Date(),
  }
}

export async function setCommunityCommentVote(params: {
  readonly postId: string
  readonly commentId: string
  readonly userId: string
  readonly pressedDirection: CommunityVoteDirection
}): Promise<{ readonly resultingDirection: CommunityVoteDirection | null }> {
  return communityApiPost<{ resultingDirection: CommunityVoteDirection | null }>(
    communityApiEndpoints.commentVote,
    {
      postId: params.postId,
      commentId: params.commentId,
      direction: params.pressedDirection,
    },
    true,
  )
}

export async function fetchCommunityVoteDirectionsForCommentsAndUser(params: {
  readonly postId: string
  readonly commentIds: readonly string[]
  readonly userId: string
}): Promise<ReadonlyMap<string, CommunityVoteDirection>> {
  if (params.commentIds.length === 0 || !params.userId) return new Map()

  const response = await communityApiPost<{ directions: Record<string, CommunityVoteDirection> }>(
    communityApiEndpoints.commentVoteDirections,
    {
      postId: params.postId,
      commentIds: [...params.commentIds],
    },
    true,
  )

  return new Map(Object.entries(response.directions))
}
